/**
 * Mirrors the filter sidebar's active-filter pills into the collection nav bar.
 *
 * collection-filters-form.js redraws the sidebar after every filter change but leaves
 * the nav bar alone, so this element watches the sidebar and copies the pills across.
 * It handles its own clicks, because pills added after the form last bound its links
 * would otherwise do nothing.
 */
(() => {
  const selectors = {
    section: '[data-section-id]',
    sidebar: '[data-collection-sidebar]',
    activeFiltersList: '[data-active-filters-list]',
    filtersForm: 'collection-filters-form',
    formBound: '[data-filter-update-url]',
    link: 'a[href]',
  };

  class CollectionActiveFilters extends HTMLElement {
    constructor() {
      super();

      this.onClickEvent = (event) => this.onClick(event);
    }

    connectedCallback() {
      this.section = this.closest(selectors.section);
      this.sidebar = this.section ? this.section.querySelector(selectors.sidebar) : null;

      this.releasePills();
      this.addEventListener('click', this.onClickEvent);

      if (!this.sidebar) return;

      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.sidebar, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClickEvent);

      if (this.observer) this.observer.disconnect();
    }

    sync() {
      const source = this.sidebar.querySelector(selectors.activeFiltersList);
      const markup = source ? source.innerHTML.trim() : '';

      if (this.innerHTML.trim() === markup) return;

      this.innerHTML = markup;
      this.releasePills();
    }

    /**
     * Drops the hook collection-filters-form binds to, so it never double-handles a
     * pill this element already listens for.
     */
    releasePills() {
      this.querySelectorAll(selectors.formBound).forEach((pill) => {
        pill.removeAttribute('data-filter-update-url');
      });
    }

    onClick(event) {
      const pill = event.target.closest(selectors.link);
      if (!pill || !this.contains(pill)) return;

      const form = this.section ? this.section.querySelector(selectors.filtersForm) : null;

      // Without the form, let the pill fall back to a plain page load.
      if (!form || typeof form.submitForm !== 'function') return;

      event.preventDefault();
      form.submitForm(event, pill.getAttribute('href'));
    }
  }

  if (!customElements.get('collection-active-filters')) {
    customElements.define('collection-active-filters', CollectionActiveFilters);
  }
})();
